import { compareDecimalStrings, isDecimalString, trimTrailingZeros } from "@/shared/utils/decimal";

import type { OtcVolumeDiscount, OtcVolumeSuggestion } from "../types/cryptoSwaps.types";
import { roundsToZero, type AmountPrecision } from "./cryptoSwapFormat";

const CENTS_BELOW = "10";

export function typedAmount(value: string): string {
  return trimTrailingZeros(value.trim());
}

export function perUnitPrecision(value: string): AmountPrecision {
  const trimmed = value.trim();
  const magnitude = trimmed.startsWith("-") ? trimmed.slice(1) : trimmed;
  const small = isDecimalString(magnitude) && compareDecimalStrings(magnitude, CENTS_BELOW) < 0;
  return { min: 0, max: small ? 2 : 0 };
}

export function showsPerUnit(value: string | null | undefined): boolean {
  if (!value || !isDecimalString(value) || compareDecimalStrings(value, "0") <= 0) return false;
  return !roundsToZero(value, perUnitPrecision(value).max);
}

export function savesPerUnit(suggestion: OtcVolumeSuggestion): boolean {
  return showsPerUnit(suggestion.savings_per_unit);
}

export function addsUnits(suggestion: OtcVolumeSuggestion): boolean {
  return (
    isDecimalString(suggestion.extra_receive) && compareDecimalStrings(suggestion.extra_receive, "0") > 0
  );
}

export function meterPercent(insight: OtcVolumeDiscount): number | null {
  const percent = Number(insight.discount?.percent);
  if (!insight.discount || !Number.isFinite(percent)) return null;
  return Math.min(100, Math.max(0, percent));
}

// Above this share the remaining discount is a few pesos per dollar; "you are at the best price" is the honest read.
export const NEAR_BEST_PERCENT = 97;

export interface VolumeDiscountView {
  insight: OtcVolumeDiscount;
  stale: boolean;
}

export function volumeDiscountView(
  insight: OtcVolumeDiscount | null,
  quoteId: string | null,
  isPricing: boolean,
): VolumeDiscountView | null {
  if (!insight || !insight.show) return null;
  if (!quoteId && !isPricing) return null;
  return { insight, stale: isPricing || insight.quote_id !== quoteId };
}
